import apiClient from "./axios";

/**
 * 附件相关的API调用
 */
export const attachmentApi = {
    /**
     * 获取预签名上传URL
     * @param {Object} fileInfo 文件信息
     * @param {string} fileInfo.filename 文件名
     * @param {string} fileInfo.content_type 文件类型
     * @param {number} fileInfo.file_size 文件大小
     * @returns {Promise<Object>} 预签名上传信息
     */
    getPresignedUploadUrl: (fileInfo) => {
        return apiClient.post("/v1/attachments/presigned-upload", fileInfo);
    },

    /**
     * 从内容块生成附件
     * @param {Object} requestData 生成请求数据
     * @param {string} requestData.content_block_type 内容块类型
     * @param {Object} requestData.content_block_data 内容块数据
     * @param {string} requestData.export_service 导出服务
     * @param {string} requestData.filename 文件名（可选）
     * @returns {Promise<Object>} 生成的附件信息（已处理success/message格式）
     */
    generateAttachmentFromContentBlock: async (requestData) => {
        const response = await apiClient.post("/v1/attachments/generate-from-content-block", requestData);

        // 添加调试日志
        console.log("[generateAttachmentFromContentBlock] API响应:", {
            status: response.status,
            data: response.data,
            requestData,
        });

        // 检查API响应是否成功
        if (!response.data.success) {
            throw new Error(response.data.message || "生成附件失败");
        }

        // 返回实际的附件数据
        // 补齐 object_key 别名：后端按 API 规范返回 storage_path，
        // 而附件 UI（AttachmentList/EmailEditorForm）按 object_key 定位删除/去重
        const attachmentData = response.data.data;
        if (attachmentData && !attachmentData.object_key && attachmentData.storage_path) {
            attachmentData.object_key = attachmentData.storage_path;
        }
        return attachmentData;
    },

    /**
     * 获取附件信息
     * @param {string} objectKey OSS对象键
     * @returns {Promise<Object>} 附件信息
     */
    getAttachmentInfo: (objectKey) => {
        // 对objectKey进行URL编码，因为它可能包含特殊字符
        const encodedObjectKey = encodeURIComponent(objectKey);
        return apiClient.get(`/v1/attachments/info/${encodedObjectKey}`);
    },

    /**
     * 删除附件
     * @param {string} objectKey OSS对象键
     * @returns {Promise<Object>} 删除结果
     */
    deleteAttachment: (objectKey) => {
        // 对objectKey进行URL编码，因为它可能包含特殊字符
        const encodedObjectKey = encodeURIComponent(objectKey);
        return apiClient.delete(`/v1/attachments/${encodedObjectKey}`);
    },

    /**
     * 获取查看/下载的预签名URL（用于私有桶的安全访问）
     * @param {string} objectKey OSS对象键（包含项目文件夹前缀）
     * @param {number} expiresIn 链接有效期（秒），默认300秒
     * @returns {Promise<Object>} 包含签名链接的数据
     */
    getPresignedViewUrl: async (objectKey, expiresIn = 300) => {
        const encodedObjectKey = encodeURIComponent(objectKey);
        const response = await apiClient.get(`/v1/attachments/presigned-view/${encodedObjectKey}`, { params: { expires_in: expiresIn } });
        if (!response.data?.success) {
            throw new Error(response.data?.message || "获取预签名查看链接失败");
        }
        return response.data.data; // { object_key, signed_url, expires_in }
    },
};

/**
 * 直接上传文件到OSS
 * @param {Object} presignedInfo 预签名信息
 * @param {File} file 要上传的文件
 * @param {Function} onProgress 上传进度回调
 * @returns {Promise<Object>} 上传结果
 */
export const uploadFileToOSS = async (presignedInfo, file, onProgress) => {
    // 验证预签名信息
    if (!presignedInfo || !presignedInfo.upload_url) {
        throw new Error("预签名信息不完整");
    }

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // 设置上传进度监听
        if (onProgress) {
            xhr.upload.addEventListener("progress", (event) => {
                if (event.lengthComputable) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    onProgress(percentComplete);
                }
            });
        }

        xhr.addEventListener("load", () => {
            if (xhr.status === 200) {
                // 构造下载URL（移除查询参数）
                const downloadUrl = presignedInfo.upload_url.split("?")[0];

                resolve({
                    success: true,
                    filename: presignedInfo.original_filename,
                    storage_path: presignedInfo.object_key, // 统一使用storage_path
                    object_key: presignedInfo.object_key, // 附件 UI（删除/去重/下载）按 object_key 定位，必须同时携带
                    content_type: file.type,
                    file_size: file.size,
                    download_url: downloadUrl, // 统一使用download_url字段，遵循API规范
                });
            } else {
                reject(new Error(`上传失败: ${xhr.status} ${xhr.statusText}`));
            }
        });

        xhr.addEventListener("error", () => {
            reject(new Error("上传过程中发生网络错误"));
        });

        xhr.addEventListener("timeout", () => {
            reject(new Error("上传超时"));
        });

        // 设置超时时间（5分钟）
        xhr.timeout = 5 * 60 * 1000;

        // 使用PUT方法直接上传文件
        xhr.open("PUT", presignedInfo.upload_url);

        // 使用后端返回的Content-Type，如果没有则使用文件的type
        const contentType = presignedInfo.content_type || file.type;
        xhr.setRequestHeader("Content-Type", contentType);

        xhr.send(file);
    });
};

/**
 * 完整的文件上传流程
 * @param {File} file 要上传的文件
 * @param {Function} onProgress 上传进度回调
 * @param {boolean} isTemporary 是否为临时文件（默认 false）。临时文件会上传到 temp/ 路径，配合 OSS 生命周期规则自动清理
 * @returns {Promise<Object>} 上传结果
 */
export const uploadFile = async (file, onProgress, isTemporary = false) => {
    try {
        // 1. 获取预签名URL
        const response = await attachmentApi.getPresignedUploadUrl({
            filename: file.name,
            content_type: file.type,
            file_size: file.size,
            is_temporary: isTemporary, // 传递临时文件标记
        });

        // 检查API响应是否成功
        if (!response.data.success) {
            throw new Error(response.data.message || "获取预签名URL失败");
        }

        // 从API响应中提取预签名信息
        const presignedInfo = response.data.data;

        // 2. 直接上传到OSS
        const uploadResult = await uploadFileToOSS(presignedInfo, file, onProgress);

        return uploadResult;
    } catch (error) {
        console.error("文件上传失败:", error);
        throw error;
    }
};

// 向后兼容的导出
export const getPresignedUploadUrl = attachmentApi.getPresignedUploadUrl;
export const generateAttachmentFromContentBlock = attachmentApi.generateAttachmentFromContentBlock;
export const getAttachmentInfo = attachmentApi.getAttachmentInfo;
export const deleteAttachment = attachmentApi.deleteAttachment;
export const getPresignedViewUrl = attachmentApi.getPresignedViewUrl;
